import DialogModal from '@/modules/common/components/Modal/DialogModal';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import { RegistrationContext } from '@/modules/RegisterLayout/contexts/RegistrationContextProvider';
import { Dropdown, InputField, Typography } from '@pw-tech/omni-ui';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
type Props = {
  compititionExam: any;
};
const CompetitiveExamDetails = ({
  compititionExam
}: Props) => {
  const {
    registrationDetails
  } = useContext(RegistrationContext);
  const {
    selectedCourseData
  } = useContext(CourseContext);
  const [directAdmissionEligibility, setDirectAdmissionEligibility] = useState<any>([]);
  const [showDirectEligibility, setShowDirectEligibility] = useState<any>(false);
  const {
    control,
    formState: {
      isSubmitted
    },
    setValue,
    getValues
  } = useFormContext();
  const {
    fields,
    replace
  } = useFieldArray({
    control,
    name: 'competitiveExams'
  });

  // Watch all competitive exams as a single array
  const competitiveExams = useWatch({
    control,
    name: 'competitiveExams'
  });
  const examDetails = useMemo(() => {
    return fields.map((_, index) => {
      const examName = competitiveExams?.[index]?.name;
      return compititionExam?.find((d: any) => d.name === examName) ?? '';
    });
  }, [fields, competitiveExams, compititionExam]);
  useEffect(() => {
    if (!Array.isArray(competitiveExams)) return;
    if (competitiveExams.length === 0) {
      replace([{
        name: '',
        score: '',
        nestedKeyValue: ''
      }]);
      return;
    }
    if (competitiveExams.length > 1) {
      const firstValidExam = competitiveExams.find((exam: any) => exam?.name) || competitiveExams[0];
      replace([{
        name: firstValidExam?.name || '',
        score: firstValidExam?.score || '',
        nestedKeyValue: firstValidExam?.nestedKeyValue || ''
      }]);
    }
  }, [competitiveExams, replace]);
  const handleShowDirectEligibility = useCallback(() => {
    if (selectedCourseData?.directAdmissionEligibility?.length) {
      setDirectAdmissionEligibility(selectedCourseData?.directAdmissionEligibility);
    } else {
      setDirectAdmissionEligibility([]);
    }
    setShowDirectEligibility(true);
  }, [selectedCourseData]);
  return <>
      <div className="flex flex-col justify-between gap-4 sm:!items-center sm:!flex-row">
        <Typography variant="heading4" weight="semi-bold" data-sentry-element="Typography" data-sentry-source-file="CompetitiveExamDetails.tsx">
          Competitive Exams Attempted
        </Typography>
        {!selectedCourseData?.skipCeeExam && <Typography component="p" variant="regular" className="!text-blue-600 underline cursor-pointer !text-sm" onClick={handleShowDirectEligibility}>
            Fast Track Process Eligibility
          </Typography>}
      </div>
      <div className="flex flex-col w-full gap-4 my-2">
        {fields?.map((exam: any, index: number) => {
        const currentExamDetails = examDetails[index];
        const selectedExamName = competitiveExams?.[index]?.name;
        // MBA (skipCeeExam): exam + related fields are optional.
        // CEE: exam optional, but score/nested required once an exam is selected.
        const isMbaFlow = !!selectedCourseData?.skipCeeExam;
        const canClearSelection = !!selectedExamName && !registrationDetails?.isPaid;
        const handleClearSelection = () => {
          setValue(`competitiveExams.${index}.name`, '', {
            shouldValidate: true
          });
          setValue(`competitiveExams.${index}.score`, '', {
            shouldValidate: true
          });
          setValue(`competitiveExams.${index}.nestedKeyValue`, '', {
            shouldValidate: true
          });
        };
        return <div key={exam.id}>
              <div className="relative flex flex-col md:!flex-row gap-4 items-start">
                {/* Exam Selection */}
                <div className="w-full">
                  <Controller name={`competitiveExams.${index}.name`} control={control} render={({
                field,
                fieldState
              }) => <Dropdown {...field} label="Select Exam You Appeared" placeholder="Select Exam You Appeared" options={compititionExam?.map((option: any) => {
                return {
                  label: option.name,
                  value: option.name
                };
              })} fullWidth required={false} onChange={(e: any) => {
                const selectedValue = e?.target?.value ?? e;
                field.onChange(selectedValue);
                // Reset nested field when exam changes
                setValue(`competitiveExams.${index}.nestedKeyValue`, '', {
                  shouldValidate: true
                });
                setValue(`competitiveExams.${index}.score`, '', {
                  shouldValidate: true
                });
              }} error={fieldState.invalid && (isSubmitted || fieldState.isTouched)} message={fieldState.error?.message} disabled={registrationDetails?.isPaid} />} />
                </div>

                <div className="w-full">
                  <Controller name={`competitiveExams.${index}.score`} control={control} rules={{
                validate: val => {
                  const formValues = getValues('competitiveExams');
                  const exam = formValues?.[index];
                  const examName = exam?.name;
                  const selectedExamDetails = compititionExam?.find((d: any) => d.name === examName);

                  // No exam selected — score not required
                  if (!examName || !examName.trim()) {
                    return true;
                  }
                  if (!selectedExamDetails) {
                    return true;
                  }

                  // MBA: score optional; only validate format/range when entered
                  if (isMbaFlow) {
                    if (val === undefined || val === null || String(val).trim() === '') {
                      return true;
                    }
                  } else if (!val && val !== 0) {
                    // CEE: score required once exam is selected
                    return `Please enter marks for ${selectedExamDetails.name}`;
                  }
                  if (Number.isNaN(Number(val))) {
                    return 'Please enter a valid number';
                  }
                  if (selectedExamDetails.greaterThan !== undefined && Number(val) <= selectedExamDetails.greaterThan) {
                    return `Marks should be more than ${selectedExamDetails.greaterThan}`;
                  }
                  if (selectedExamDetails.lessThan !== undefined && Number(val) >= selectedExamDetails.lessThan) {
                    return `Marks should be less than ${selectedExamDetails.lessThan}`;
                  }
                  return true;
                }
              }} render={({
                field,
                fieldState
              }) => <InputField {...field} label={currentExamDetails?.placeholder || 'Enter Score'} type="text" error={fieldState.invalid && (isSubmitted || fieldState.isTouched || field.value || field.value === '')} message={fieldState.error?.message} disabled={registrationDetails?.isPaid || !currentExamDetails?.name} fullWidth />} />
                </div>

                {/* Additional Nested Fields */}
                {currentExamDetails?.additionalNestedFields && Object.entries(currentExamDetails.additionalNestedFields).map(([fieldKey, fieldConfig]: [string, any]) => <div className="w-full" key={fieldKey}>
                        <Controller name={`competitiveExams.${index}.nestedKeyValue`} control={control} rules={{
                validate: val => {
                  // MBA: nested fields optional
                  if (isMbaFlow) return true;
                  // CEE: required once exam is selected
                  const formValues = getValues('competitiveExams');
                  const exam = formValues?.[index];
                  if (!exam?.name || !exam.name.trim()) return true;
                  if (!val) return `Please select ${fieldKey}`;
                  return true;
                }
              }} render={({
                field,
                fieldState
              }) => <Dropdown {...field} label={`${fieldKey}`} placeholder={`Select ${fieldKey}`} options={fieldConfig.values?.map((v: string) => ({
                label: v,
                value: v
              }))} fullWidth error={fieldState.invalid && (isSubmitted || fieldState.isTouched)} message={fieldState.error?.message} disabled={registrationDetails?.isPaid} />} />
                      </div>)}
              </div>
              {canClearSelection && <div className="mt-2 flex justify-end">
                  <button type="button" onClick={handleClearSelection} aria-label="Clear competitive exam selection" className="group inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                    Clear selection
                  </button>
                </div>}
            </div>;
      })}
      </div>

      {/* Direct Admission Eligibility Modal */}
      <DialogModal isOpen={showDirectEligibility} title="Eligibility Criteria For Direct Admission" onClose={() => {
      setShowDirectEligibility(false);
    }} onSubmit={() => {
      setShowDirectEligibility(false);
    }} showHeader showSeparator={false} showFooter={false} showCancelButton={false} showSubmitButton={false} showCloseIcon size="extra-small" data-sentry-element="DialogModal" data-sentry-source-file="CompetitiveExamDetails.tsx">
        <div className="w-full h-auto">
          <div className="flex flex-col gap-2 text-center">
            <Typography weight="semi-bold" className="text-start" data-sentry-element="Typography" data-sentry-source-file="CompetitiveExamDetails.tsx">
              Fast Track Process criteria are based on either Class 10 & 12 marks OR exam scores.
            </Typography>
            <div className="flex flex-col gap-2">
              {directAdmissionEligibility?.map((data: any) => <div key={data?.name} className="flex items-center gap-2">
                  <Typography variant="heading4" weight="semi-bold">
                    {data?.name} &nbsp;:
                  </Typography>
                  <Typography variant="regular">
                    {data?.score} {data?.type}
                  </Typography>
                </div>)}
            </div>
          </div>
        </div>
      </DialogModal>
    </>;
};
export default CompetitiveExamDetails;